/**
 * PageLoader — CaCart branded in-page loader for API/data loading states.
 * Shows CaCart logo, animated vegetable icons, and progress bar.
 *
 * Usage:
 *   if (loading) return <PageLoader />;
 *   if (loading) return <PageLoader height="40vh" />;
 *   if (loading) return <PageLoader height="30vh" compact />;
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import LogoMain from '../../Images/logo_main_black.webp';

interface PageLoaderProps {
  height?: string;
  message?: string;
  compact?: boolean;
}

const PageLoader: React.FC<PageLoaderProps> = ({
  height = "60vh",
  message = "Loading fresh data...",
  compact = false,
}) => {
  const logoSize = compact ? 100 : 140;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height={height}
      sx={{ userSelect: "none" }}
    >
      {/* Logo with float animation */}
      <Box sx={{
        position: "relative", mb: compact ? 1.5 : 2.5,
        animation: "plFloat 2.4s ease-in-out infinite",
        "@keyframes plFloat": {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-6px)" },
        },
      }}>
        {/* Glow */}
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: logoSize * 0.8, height: logoSize * 0.8, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,131,143,0.1) 0%, transparent 70%)",
          animation: "plGlow 2.4s ease-in-out infinite",
          "@keyframes plGlow": {
            "0%, 100%": { opacity: 0.3, transform: "translate(-50%, -50%) scale(1)" },
            "50%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.3)" },
          },
        }} />
        <img
          src={LogoMain}
          alt="CaCart"
          style={{ width: logoSize, height: "auto", position: "relative", zIndex: 1 }}
        />
      </Box>

      {/* Vegetable icons row */}
      {!compact && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1.5, mb: 2,
          animation: "plFadeUp 0.6s ease-out",
          "@keyframes plFadeUp": {
            "0%": { opacity: 0, transform: "translateY(6px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}>
          {[
            { emoji: "🥕", delay: "0s" },
            { emoji: "🥬", delay: "0.15s" },
            { emoji: "🍅", delay: "0.3s" },
            { emoji: "🥦", delay: "0.45s" },
            { emoji: "🌽", delay: "0.6s" },
          ].map((v, i) => (
            <Box key={i} sx={{
              fontSize: compact ? 16 : 20,
              opacity: 0,
              animation: "plVegPop 0.4s ease-out forwards, plVegBounce 2s ease-in-out infinite",
              animationDelay: `${v.delay}, ${parseFloat(v.delay) + 0.8}s`,
              "@keyframes plVegPop": {
                "0%": { opacity: 0, transform: "scale(0.3)" },
                "70%": { transform: "scale(1.15)" },
                "100%": { opacity: 1, transform: "scale(1)" },
              },
              "@keyframes plVegBounce": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(-3px)" },
              },
            }}>
              {v.emoji}
            </Box>
          ))}
        </Box>
      )}

      {/* Progress bar */}
      <Box sx={{ width: compact ? 90 : 130, height: 3, borderRadius: 2, bgcolor: "#e2e8f0", overflow: "hidden", mb: 1.5 }}>
        <Box sx={{
          width: "35%", height: "100%", borderRadius: 2,
          background: "linear-gradient(90deg, #00838f, #00acc1, #00838f)",
          backgroundSize: "200% 100%",
          animation: "plSlide 1.4s ease-in-out infinite",
          "@keyframes plSlide": {
            "0%": { transform: "translateX(-100%)" },
            "100%": { transform: "translateX(350%)" },
          },
        }} />
      </Box>

      {/* Message with icons */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.75, opacity: 0.4,
        animation: "plMsgIn 0.8s ease-out 0.3s both",
        "@keyframes plMsgIn": {
          "0%": { opacity: 0 },
          "100%": { opacity: 0.4 },
        },
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="#94a3b8"/>
        </svg>
        <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 500, letterSpacing: "0.03em" }}>
          {message}
        </Typography>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M17.5 3C15.57 3 12.61 4.86 11.29 7.31 9.97 5.04 7.17 3.32 5.5 3 4.5 3 3 4 3 6c0 4 3.5 7.5 8 10.31V21h2v-4.69C17.5 13.5 21 10 21 6c0-2-1.5-3-3.5-3z" fill="#94a3b8"/>
        </svg>
      </Box>
    </Box>
  );
};

export { PageLoader };
export default PageLoader;
